import {
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    output,
    Output,
    QueryList,
    SimpleChanges,
    ViewChildren,
    ChangeDetectionStrategy,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { S25DropdownPaginatedModule } from "../s25-dropdown/s25.dropdown.paginated.module";
import { S25InfoMessageModule } from "../s25-info-message/s25.info.message.module";
import { S25SimpleCollapseModule } from "../s25-simple-collapse/s25.simple.collapse.module";
import { S25QLConst } from "./s25ql.const";
import { Item } from "../../pojo/Item";
import { DropDownItem } from "../../pojo/DropDownItem";
import { SearchCriteria } from "../../pojo/SearchCriteria";
import { AdvancedSearchUtil } from "../advanced-search/advanced-search-util";
import { Tokenizer } from "./s25ql.tokenizer";
import { S25Util } from "../../util/s25-util";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { Proto } from "../../pojo/Proto";
import { S25qlSearchAdvancedCriterionComponent } from "./s25ql.search.advanced.criterion.component";
import { S25Const } from "../../util/s25-const";
import { QLUtil } from "./s25ql.util";
import { SearchService } from "../../services/search/search.service";
import Model = SearchCriteria.Model;
import Searches = SearchCriteria.Searches;
import Step = SearchCriteria.Step;
import StepType = SearchCriteria.StepType;
import NumericalString = Proto.NumericalString;

@TypeManagerDecorator("s25-ng-ql-search-advanced-criteria")
@Component({
    selector: "s25-ng-ql-search-advanced-criteria",
    template: `
        @if (isInit) {
            <s25-simple-collapse
                [header]="'Query'"
                [hasClose]="hasClose"
                (closed)="onClose()"
                [class.hasValidationError]="validationError"
            >
                <div class="criteriaWrapper">
                    <div class="criteriaRow">
                        <label for="conjunction">Conjunction</label>
                        <select
                            id="conjunction"
                            [(ngModel)]="model.query_method"
                            (ngModelChange)="change.emit()"
                            class="cn-form__control"
                            [disabled]="isContactSearch"
                            [title]="
                                isContactSearch
                                    ? 'Contact searches only support AND conjunction. All criteria must match.'
                                    : ''
                            "
                            [attr.aria-label]="isContactSearch ? 'Conjunction (AND only for contacts)' : 'Conjunction'"
                        >
                            <option value="all">and</option>
                            <option value="any">or</option>
                        </select>
                        @if (isContactSearch) {
                            <s25-ng-info-message>Contact searches only support AND conjunction</s25-ng-info-message>
                        }
                    </div>
                    @for (step of model.step; track step; let i = $index) {
                        @if (isTempSearch(step)) {
                            <s25-ng-ql-search-advanced-criteria
                                #advancedCriteria
                                [type]="type"
                                [hasClose]="true"
                                (closed)="removeStep(i)"
                                (change)="change.emit()"
                                [model]="getSearchModel(step.step_param[0].itemName)"
                                [searches]="searches"
                            ></s25-ng-ql-search-advanced-criteria>
                        }
                        @if (!isTempSearch(step)) {
                            <s25-ng-ql-search-advanced-criterion
                                #advancedCriterion
                                [type]="type"
                                [step]="step"
                                [allSteps]="model.step"
                                (closed)="removeStep(i)"
                                (change)="change.emit()"
                            ></s25-ng-ql-search-advanced-criterion>
                        }
                    }
                    <s25-dropdown-paginated
                        class="addCriteriaButton"
                        [model]="dropdownModel"
                        [chosen]="chosen"
                        [choice]="choice"
                        [arrayChoice]="choice"
                        [ariaLabel]="'Add Search Criterion'"
                        (chosenChange)="addStep($event)"
                    ></s25-dropdown-paginated>
                    @if (validationError) {
                        <p class="validationError">
                            {{ validationError }}
                        </p>
                    }
                </div>
            </s25-simple-collapse>
        }

        <ng-template #choice let-item="item">
            <div class="{{ item.iconClass }}">{{ item.itemName }}</div>
        </ng-template>
    `,
    styles: `
        .criteriaWrapper {
            padding: 0.5em 0 0.5em 1em;
        }

        .criteriaRow {
            display: flex;
            gap: 0.5em;
            align-items: center;
        }

        .criteriaRow > label {
            margin: auto 0;
        }

        .criteriaRow s25-ng-info-message {
            margin-left: 0.5em;
        }

        .addCriteriaButton {
            width: 9em;
            display: block;
            margin-top: 1em;
        }

        :host ::ng-deep .addCriteriaButton .select2-choice::after {
            display: none;
        }

        :host ::ng-deep .addCriteriaButton .select2-drop {
            width: 24em !important;
            left: 0;
        }

        :host ::ng-deep .addCriteriaButton .select2-results {
            max-height: Min(60vh, 800px);
        }

        :host s25-simple-collapse ::ng-deep s25-simple-collapse {
            border-right: 0;
            border-top-right-radius: 0;
            border-bottom-right-radius: 0;
        }

        .validationError {
            padding-top: 1em;
            color: #c00;
            font-size: 0.9em;
        }

        ::ng-deep s25-ng-ql-search-advanced-criteria .hasValidationError s25-simple-collapse {
            border: 1px solid #c00 !important;
        }

        ::ng-deep .nm-party--on s25-ng-ql-search-advanced-criteria .validationError {
            color: #ff8b8b;
        }

        ::ng-deep .nm-party--on s25-ng-ql-search-advanced-criteria .hasValidationError s25-simple-collapse {
            border: 1px solid #ff8b8b !important;
        }
    `,
    standalone: true,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        S25SimpleCollapseModule,
        S25InfoMessageModule,
        S25DropdownPaginatedModule,
        S25qlSearchAdvancedCriterionComponent,
        FormsModule,
    ],
})
export class S25qlSearchAdvancedCriteriaComponent implements OnChanges, OnInit {
    @Input() type: Tokenizer.QLItemTypes;
    @Input() hasClose: boolean = false;
    @Input() model: AdvancedModel;
    @Input() searches: Searches;

    @Output() closed = new EventEmitter<void>();
    readonly change = output<void>();

    @ViewChildren("advancedCriteria") advancedCriteriaComponents: QueryList<S25qlSearchAdvancedCriteriaComponent>;
    @ViewChildren("advancedCriterion") advancedCriterionComponents: QueryList<S25qlSearchAdvancedCriterionComponent>;

    chosen: {} = null;
    conjunction: "and" | "or" = "and";
    criteria: Criterion[] = [];
    dropdownModel: { items: any[]; placeholder: string };
    isInit = false;
    validationError: string;

    get isContactSearch(): boolean {
        return SearchService.usesSubscriptionSets(this.type);
    }

    getSearchModel(searchKey: string): AdvancedModel {
        return this.searches[searchKey] as unknown as AdvancedModel;
    }

    constructor(private changeDetector: ChangeDetectorRef) {}

    ngOnChanges(changes: SimpleChanges) {
        if (changes.type && !changes.type.isFirstChange()) this.updateDropdownModel();
    }

    async ngOnInit() {
        this.updateDropdownModel();

        // Contact searches only support AND conjunction
        if (this.isContactSearch) {
            this.model.query_method = "all";
        }

        this.isInit = true;
    }

    updateDropdownModel() {
        const items = S25QLConst.advancedUIOptions[this.type];
        this.dropdownModel = { items, placeholder: "Add Criterion" };
    }

    addStep(data: DropDownItem) {
        this.clearValidationError();
        this.chosen = {}; // Reset Chosen
        const { itemId, itemName, stepTypeId, subStepTypeId, iconClass } = data;

        // For contacts, check if this step type already exists before adding
        if (this.isContactSearch && stepTypeId >= 0) {
            // Create a temporary step to check if adding it would create a duplicate
            const tempStep: Step = AdvancedSearchUtil.getStep(this.type, stepTypeId, subStepTypeId);
            const stepsWithNewStep = [...this.model.step, tempStep];
            const duplicateError = QLUtil.checkForDuplicateContactParameters(stepsWithNewStep, this.type);
            if (duplicateError) {
                this.validationError = duplicateError;
                return;
            }
        }

        if (stepTypeId < 0) {
            const tempSearchKey = `temp_${S25Util.generateQuickGUID()}`;
            const step_type_id = this.type === Item.Ids.Event ? StepType.Event.EventSearch : this.type * 100 + 5; // All others are X05
            const step: Step = {
                ...AdvancedSearchUtil.s25SearchAdvancedStepTemplate[S25Const.itemId2Name[this.type]][step_type_id],
                step_param: [{ itemName: tempSearchKey, itemId: tempSearchKey }],
            };
            this.searches[tempSearchKey] = { query_method: "all", step: [] };
            this.model.step.push(step);
        } else {
            const step: AdvancedStep = AdvancedSearchUtil.getStep(this.type, stepTypeId, subStepTypeId);
            step.sub_step_type_id = subStepTypeId;
            step.name = itemName;
            this.model.step.push(step);
        }
        this.change.emit();

        this.changeDetector.detectChanges();
    }

    removeStep(index: number) {
        this.clearValidationError();
        this.model.step.splice(index, 1);
        this.change.emit();
    }

    onClose() {
        this.closed.emit();
    }

    isTempSearch(step: Step): boolean {
        return QLUtil.isStepTemp(step);
    }

    clearValidationError() {
        this.validationError = null;
    }

    validate(): boolean {
        let ok = true;
        if (!this.model.step.length) {
            ok = false;
            this.validationError = "Please add at least one criterion.";
        }

        // For contacts, check for duplicate parameters (only one of each parameter type allowed)
        if (this.isContactSearch) {
            // Collect all steps from main model and nested searches
            const allSteps: Step[] = [];

            // Check main model steps
            for (const step of this.model.step) {
                allSteps.push(step);
            }

            // Check nested searches
            for (const searchKey in this.searches) {
                const search = this.searches[searchKey];
                for (const step of search.step || []) {
                    allSteps.push(step);
                }
            }

            const duplicateError = QLUtil.checkForDuplicateContactParameters(allSteps, this.type);
            if (duplicateError) {
                ok = false;
                this.validationError = duplicateError;
            }
        }

        for (const criteria of this.advancedCriteriaComponents.toArray()) {
            ok = criteria.validate() && ok;
        }
        for (const criterion of this.advancedCriterionComponents.toArray()) {
            ok = criterion.validate() && ok;
        }
        return ok;
    }
}

export type AdvancedModel = Model & { step: AdvancedStep[] };

export type AdvancedStep = Step & {
    sub_step_type_id: number | NumericalString;
    name: string;
    relationship_type_id?: number | NumericalString;
};

export type Criterion = {
    itemId: string | number;
    itemName: string;
    stepTypeId: number;
    subStepTypeId?: number;
    iconClass?: string;
};
